/**
 * PhotoGallery — galleria foto di una scheda lavoro (Fase 5 design foto).
 *
 * Responsabilità:
 *   - Mostra tutte le foto della scheda raggruppate per fase (accettazione /
 *     lavorazione / riconsegna), con filtri e conteggio "Foto (N/100)".
 *   - Visualizzatore a schermo intero: Image.PreviewGroup di Ant Design
 *     alimentato con blob URL autenticati via useAuthenticatedImage.
 *   - Eliminazione con logica R6: proprietario (uploaded_by_user_id) OPPURE
 *     work_order_photos.delete OPPURE ADMIN.
 *   - Acquisizione delegata a PhotoCaptureManager (Scatta / Carica) dentro
 *     un modale dedicato, con selezione di fase e intervento.
 *
 * Non ancora montato in SchedaLavoroV2: verrà inserito in Fase 6.
 */

import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Image,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd'
import {
  CameraOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownOutlined,
  LeftOutlined,
  PictureOutlined,
  RightOutlined,
  UpOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import axiosInstance from '@/lib/axios'
import { useAuth } from '@/contexts/AuthContext'
import { usePermission } from '@/hooks/usePermission'
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage'
import { PhotoCaptureManager, PhotoCaptureManagerHandle } from '@/components/PhotoCaptureManager'
import type { Intervention } from '@/types'

const { Text } = Typography

// ─── Tipi ────────────────────────────────────────────────────────────────────

type FaseFoto = 'accettazione' | 'lavorazione' | 'riconsegna'

interface WorkOrderPhotoFull {
  id: number
  file_name: string
  /** URL proxy thumbnail: /api/v1/work-orders/{id}/photos/{pid}/image */
  thumbnail_url: string
  /** URL proxy originale: /api/v1/work-orders/{id}/photos/{pid}/image-original */
  google_drive_url: string
  source: 'webcam' | 'upload'
  created_at: string
  fase: FaseFoto
  intervention_id: number | null
  uploaded_by_user_id: number | null
}

export interface PhotoGalleryProps {
  workOrderId: number
  /** Lista interventi della scheda, per label nel filtro e nel viewer */
  interventions?: Intervention[]
  /** Se false: nessun chevron, corpo sempre visibile (comportamento V3). Default true. */
  collapsible?: boolean
}

// ─── Costanti ─────────────────────────────────────────────────────────────────

const PHOTO_LIMIT = 100
const FASI: FaseFoto[] = ['accettazione', 'lavorazione', 'riconsegna']

const FASE_LABEL: Record<FaseFoto, string> = {
  accettazione: 'Accettazione',
  lavorazione: 'Lavorazione',
  riconsegna: 'Riconsegna',
}

const FASE_COLOR: Record<FaseFoto, string> = {
  accettazione: 'blue',
  lavorazione: 'orange',
  riconsegna: 'green',
}

// ─── GalleryPhotoCard ─────────────────────────────────────────────────────────
// Sub-componente miniatura: usa useAuthenticatedImage per il thumbnail (blob URL).
// È un sotto-componente separato perché ogni miniatura chiama un hook — la regola
// degli hook vieta di chiamarlo in un .map() nel componente padre.

interface GalleryPhotoCardProps {
  photo: WorkOrderPhotoFull
  canDelete: boolean
  deleting: boolean
  onDelete: () => void
  onSelect: () => void
}

function GalleryPhotoCard({ photo, canDelete, deleting, onDelete, onSelect }: GalleryPhotoCardProps) {
  const { token } = theme.useToken()
  const { src: thumbSrc, loading: thumbLoading, error: thumbError } = useAuthenticatedImage(photo.thumbnail_url)

  return (
    <Tooltip
      title={
        <span>
          {photo.file_name}
          <br />
          {dayjs(photo.created_at).format('DD/MM/YYYY HH:mm')}
          {photo.uploaded_by_user_id != null && (
            <>
              <br />
              Caricato da: utente #{photo.uploaded_by_user_id}
            </>
          )}
        </span>
      }
    >
      <div
        style={{
          position: 'relative',
          width: 80,
          height: 80,
          flexShrink: 0,
          borderRadius: token.borderRadius,
          overflow: 'hidden',
          border: `1px solid ${token.colorBorderSecondary}`,
          cursor: 'pointer',
          backgroundColor: token.colorFillTertiary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={onSelect}
      >
        {thumbLoading && <Spin size="small" />}
        {thumbError && (
          <span style={{ fontSize: 20, color: token.colorTextDisabled }}>⚠️</span>
        )}
        {thumbSrc && (
          <img
            src={thumbSrc}
            alt={photo.file_name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {/* Etichetta fase in basso */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontSize: 9,
            textAlign: 'center',
            padding: '1px 0',
            lineHeight: '14px',
            pointerEvents: 'none',
          }}
        >
          #{photo.id}
        </div>

        {/* Pulsante Elimina — visibile solo se R6 soddisfatto */}
        {canDelete && (
          <Popconfirm
            title="Eliminare questa foto?"
            okText="Sì, elimina"
            cancelText="Annulla"
            okButtonProps={{ danger: true }}
            onConfirm={(e) => {
              e?.stopPropagation()
              onDelete()
            }}
            onCancel={(e) => e?.stopPropagation()}
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleting}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 24,
                height: 24,
                padding: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.92)',
                borderRadius: 4,
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        )}
      </div>
    </Tooltip>
  )
}

// ─── GalleryViewer ────────────────────────────────────────────────────────────
// Modale visualizzatore: carica l'originale con useAuthenticatedImage (lazy,
// solo quando il modale è aperto). Usa Image.PreviewGroup di Ant Design per
// zoom, rotazione e barra strumenti nativa.

interface GalleryViewerProps {
  photo: WorkOrderPhotoFull
  interventions: Intervention[]
  onClose: () => void
  onPrev: (() => void) | null
  onNext: (() => void) | null
  indexLabel: string  // es. "3 / 12"
}

function GalleryViewer({
  photo,
  interventions,
  onClose,
  onPrev,
  onNext,
  indexLabel,
}: GalleryViewerProps) {
  // Carica l'originale (proxy /image-original) via blob URL autenticato.
  // useAuthenticatedImage usa toAxiosPath() che rimuove il prefisso /api/v1
  // prima di passare l'URL ad axiosInstance — nessun doppio prefisso.
  const { src: origSrc, loading, error } = useAuthenticatedImage(photo.google_drive_url)

  const intervention = photo.intervention_id != null
    ? interventions.find(i => i.id === photo.intervention_id)
    : null

  return (
    <Modal
      open
      onCancel={onClose}
      title={
        <span>
          <PictureOutlined style={{ marginRight: 8 }} />
          {photo.file_name}
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
            {indexLabel}
          </Text>
        </span>
      }
      width={800}
      centered
      footer={[
        onPrev && (
          <Button key="prev" icon={<LeftOutlined />} onClick={onPrev}>
            Precedente
          </Button>
        ),
        onNext && (
          <Button key="next" icon={<RightOutlined />} onClick={onNext}>
            Successiva
          </Button>
        ),
        <Button key="close" onClick={onClose}>
          Chiudi
        </Button>,
      ].filter(Boolean)}
      bodyStyle={{ textAlign: 'center', padding: 24 }}
    >
      {/* Immagine originale tramite blob URL autenticato — Image.PreviewGroup
          abilita zoom, rotazione e download nativi di Ant Design */}
      <div style={{ marginBottom: 16, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading && <Spin size="large" tip="Caricamento…" />}
        {error && (
          <Alert type="warning" message="Impossibile caricare l'immagine originale" showIcon />
        )}
        {origSrc && !error && (
          <Image.PreviewGroup>
            <Image
              src={origSrc}
              alt={photo.file_name}
              style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 8 }}
            />
          </Image.PreviewGroup>
        )}
      </div>

      {/* Metadati foto */}
      <div style={{ textAlign: 'left', fontSize: 13 }}>
        <Tag color={FASE_COLOR[photo.fase]}>{FASE_LABEL[photo.fase]}</Tag>
        <Text type="secondary" style={{ marginLeft: 8 }}>
          {dayjs(photo.created_at).format('DD/MM/YYYY HH:mm')}
        </Text>
        {photo.uploaded_by_user_id != null && (
          <Text type="secondary" style={{ marginLeft: 8 }}>
            · Utente #{photo.uploaded_by_user_id}
          </Text>
        )}
        {intervention != null && (
          <Text type="secondary" style={{ marginLeft: 8 }}>
            · Intervento: {intervention.descrizione_intervento || `#${intervention.id}`}
          </Text>
        )}
        <Text type="secondary" style={{ marginLeft: 8 }}>
          · {photo.source === 'webcam' ? '📷 Webcam' : '📁 Upload'}
        </Text>
      </div>
    </Modal>
  )
}

// ─── PhotoGallery ─────────────────────────────────────────────────────────────

export function PhotoGallery({ workOrderId, interventions = [], collapsible = true }: PhotoGalleryProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Permessi
  const canCreate = usePermission('work_order_photos.create')
  const canDeleteAny = usePermission('work_order_photos.delete')

  // Filtri
  const [faseFilter, setFaseFilter] = useState<FaseFoto | 'all'>('all')
  const [interventionFilter, setInterventionFilter] = useState<number | null>(null)

  // Viewer
  const [viewerPhotoId, setViewerPhotoId] = useState<number | null>(null)

  // Modale acquisizione
  const [acquisitionOpen, setAcquisitionOpen] = useState(false)
  const [acquisitionFase, setAcquisitionFase] = useState<FaseFoto>('accettazione')
  const [acquisitionInterventionId, setAcquisitionInterventionId] = useState<number | null>(null)
  const captureRef = useRef<PhotoCaptureManagerHandle>(null)
  const [committing, setCommitting] = useState(false)

  // ── Fetch foto ──────────────────────────────────────────────────────────────
  const {
    data: photos = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['work-order-photos', workOrderId],
    queryFn: () =>
      axiosInstance
        .get<WorkOrderPhotoFull[]>(`/work-orders/${workOrderId}/photos`)
        .then((r) => r.data),
    enabled: workOrderId > 0,
    staleTime: 30_000,
  })

  // ── Delete foto ─────────────────────────────────────────────────────────────
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())

  const deleteMutation = useMutation({
    mutationFn: (photoId: number) =>
      axiosInstance.delete(`/work-orders/${workOrderId}/photos/${photoId}`),
    onMutate: (photoId) => {
      setDeletingIds((prev) => new Set(prev).add(photoId))
    },
    onSuccess: (_, photoId) => {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(photoId); return s })
      queryClient.invalidateQueries({ queryKey: ['work-order-photos', workOrderId] })
      message.success('Foto eliminata')
    },
    onError: (_, photoId) => {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(photoId); return s })
      message.error('Errore nell\'eliminazione della foto')
    },
  })

  // ── Logica R6 ───────────────────────────────────────────────────────────────
  // Cancellazione consentita se: l'utente ha work_order_photos.delete
  // OPPURE è il proprietario della foto.
  const canDeletePhoto = (photo: WorkOrderPhotoFull): boolean => {
    if (!user) return false
    if (canDeleteAny) return true                 // permesso esplicito
    return (                                      // proprietario
      photo.uploaded_by_user_id !== null &&
      photo.uploaded_by_user_id === user.id
    )
  }

  // ── Filtri e raggruppamento ─────────────────────────────────────────────────
  const filteredPhotos = photos.filter((p) => {
    if (faseFilter !== 'all' && p.fase !== faseFilter) return false
    if (interventionFilter !== null && p.intervention_id !== interventionFilter) return false
    return true
  })

  // Gruppi per fase nell'ordine canonico; gruppi vuoti esclusi
  const groups = FASI.map((fase) => ({
    fase,
    photos: filteredPhotos.filter((p) => p.fase === fase),
  })).filter((g) => g.photos.length > 0)

  // ── Viewer ──────────────────────────────────────────────────────────────────
  const viewerPhoto = viewerPhotoId != null
    ? filteredPhotos.find((p) => p.id === viewerPhotoId) ?? null
    : null
  const viewerIndex = viewerPhoto
    ? filteredPhotos.findIndex((p) => p.id === viewerPhotoId)
    : -1

  const openViewer = (photoId: number) => setViewerPhotoId(photoId)
  const closeViewer = () => setViewerPhotoId(null)
  const prevPhoto = viewerIndex > 0
    ? () => setViewerPhotoId(filteredPhotos[viewerIndex - 1].id)
    : null
  const nextPhoto = viewerIndex < filteredPhotos.length - 1
    ? () => setViewerPhotoId(filteredPhotos[viewerIndex + 1].id)
    : null

  // ── Acquisizione ────────────────────────────────────────────────────────────
  const photoCount = photos.length
  const canUploadMore = photoCount < PHOTO_LIMIT

  // ── Collasso ─────────────────────────────────────────────────────────────────
  const [aperto, setAperto] = useState(false)
  const autoApertoRef = useRef(false)

  useEffect(() => {
    if (photoCount > 0 && !autoApertoRef.current) {
      autoApertoRef.current = true
      setAperto(true)
    }
  }, [photoCount])

  const corpoVisibile = collapsible === false || aperto
  const summaryText = photoCount > 0 ? `${photoCount} foto` : 'nessuna caricata'

  const openAcquisition = () => setAcquisitionOpen(true)

  const handleCommitAcquisition = async () => {
    try {
      setCommitting(true)
      await captureRef.current?.commitPhotos()
      setAcquisitionOpen(false)
      queryClient.invalidateQueries({ queryKey: ['work-order-photos', workOrderId] })
    } catch {
      // commitPhotos mostra già il messaggio di errore
    } finally {
      setCommitting(false)
    }
  }

  const handleCancelAcquisition = () => {
    captureRef.current?.discardChanges()
    setAcquisitionOpen(false)
  }

  // Interventi unici presenti nelle foto (per il filtro per intervento)
  const interventionIds = Array.from(
    new Set(photos.map((p) => p.intervention_id).filter((id): id is number => id !== null))
  )
  const interventionOptions = interventionIds.map((id) => {
    const iv = interventions.find((i) => i.id === id)
    return {
      value: id,
      label: iv ? (iv.descrizione_intervento || `Intervento #${id}`) : `Intervento #${id}`,
    }
  })

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Header: titolo + filtri + pulsanti acquisizione ─── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 15, marginRight: 4 }}>
          Foto ({photoCount}/{PHOTO_LIMIT})
        </span>
        <Text type="secondary" style={{ fontSize: 12 }}>{summaryText}</Text>

        {/* Filtro per fase */}
        <Select
          size="small"
          value={faseFilter}
          onChange={setFaseFilter}
          style={{ width: 140 }}
          options={[
            { value: 'all', label: 'Tutte le fasi' },
            ...FASI.map((f) => ({ value: f, label: FASE_LABEL[f] })),
          ]}
        />

        {/* Filtro per intervento — visibile solo se ci sono foto con intervention_id */}
        {interventionOptions.length > 0 && (
          <Select
            size="small"
            value={interventionFilter ?? 'all'}
            onChange={(v) => setInterventionFilter(v === 'all' ? null : (v as number))}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'Tutti gli interventi' },
              ...interventionOptions,
            ]}
          />
        )}

        {/* Spazio elastico */}
        <div style={{ flex: 1 }} />

        {/* Pulsanti Scatta / Carica — solo se ha work_order_photos.create */}
        {canCreate && (
          <>
            <Tooltip title={!canUploadMore ? `Limite di ${PHOTO_LIMIT} foto raggiunto` : ''}>
              <Button
                icon={<CameraOutlined />}
                size="small"
                disabled={!canUploadMore}
                onClick={() => {
                  setAcquisitionFase(faseFilter !== 'all' ? faseFilter : 'accettazione')
                  setAcquisitionInterventionId(interventionFilter)
                  openAcquisition()
                }}
              >
                Scatta
              </Button>
            </Tooltip>
            <Tooltip title={!canUploadMore ? `Limite di ${PHOTO_LIMIT} foto raggiunto` : ''}>
              <Button
                icon={<CloudUploadOutlined />}
                size="small"
                disabled={!canUploadMore}
                onClick={() => {
                  setAcquisitionFase(faseFilter !== 'all' ? faseFilter : 'accettazione')
                  setAcquisitionInterventionId(interventionFilter)
                  openAcquisition()
                }}
              >
                Carica
              </Button>
            </Tooltip>
          </>
        )}
        {collapsible && (
          <Button
            type="text"
            size="small"
            icon={aperto ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setAperto(o => !o)}
          />
        )}
      </div>

      {/* ── Corpo galleria ─────────────────────────────────────── */}
      {corpoVisibile && (
        <>
          {isLoading && <Spin tip="Caricamento foto…" style={{ display: 'block', marginTop: 24 }} />}

          {isError && (
            <Alert
              type="error"
              message="Impossibile caricare le foto"
              description="Verifica i permessi o ricarica la pagina."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {!isLoading && !isError && groups.length === 0 && (
            <Text type="secondary" style={{ fontSize: 13, display: 'block', padding: '8px 0' }}>
              {photos.length === 0
                ? 'Nessuna foto ancora caricata'
                : 'Nessuna foto corrisponde ai filtri selezionati'}
            </Text>
          )}

          {/* Gruppi per fase */}
          {groups.map(({ fase, photos: groupPhotos }) => (
        <div key={fase} style={{ marginBottom: 20 }}>
          {/* Intestazione gruppo */}
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tag color={FASE_COLOR[fase]}>{FASE_LABEL[fase]}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {groupPhotos.length} foto
            </Text>
          </div>

          {/* Griglia miniature */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {groupPhotos.map((photo) => (
              <GalleryPhotoCard
                key={photo.id}
                photo={photo}
                canDelete={canDeletePhoto(photo)}
                deleting={deletingIds.has(photo.id)}
                onDelete={() => deleteMutation.mutate(photo.id)}
                onSelect={() => openViewer(photo.id)}
              />
            ))}
          </div>
        </div>
          ))}
        </>
      )}

      {/* ── Visualizzatore a schermo intero ────────────────────── */}
      {viewerPhoto && (
        <GalleryViewer
          photo={viewerPhoto}
          interventions={interventions}
          onClose={closeViewer}
          onPrev={prevPhoto}
          onNext={nextPhoto}
          indexLabel={`${viewerIndex + 1} / ${filteredPhotos.length}`}
        />
      )}

      {/* ── Modale acquisizione ─────────────────────────────────── */}
      <Modal
        open={acquisitionOpen}
        onCancel={handleCancelAcquisition}
        title="Aggiungi foto"
        width={640}
        footer={[
          <Button key="cancel" onClick={handleCancelAcquisition} disabled={committing}>
            Annulla
          </Button>,
          <Button
            key="commit"
            type="primary"
            onClick={handleCommitAcquisition}
            loading={committing}
          >
            Conferma
          </Button>,
        ]}
        destroyOnClose
      >
        {/* Selettore fase prima di acquisire */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text strong>Fase:</Text>
          <Select
            value={acquisitionFase}
            onChange={setAcquisitionFase}
            style={{ width: 160 }}
            options={FASI.map((f) => ({ value: f, label: FASE_LABEL[f] }))}
          />
          {interventions.length > 0 && (
            <>
              <Text strong style={{ marginLeft: 8 }}>Intervento:</Text>
              <Select
                value={acquisitionInterventionId ?? 'none'}
                onChange={(v) => setAcquisitionInterventionId(v === 'none' ? null : (v as number))}
                style={{ width: 200 }}
                options={[
                  { value: 'none', label: 'Nessun intervento' },
                  ...interventions
                    .filter((i) => i.id != null)
                    .map((i) => ({
                      value: i.id as number,
                      label: i.descrizione_intervento || `Intervento #${i.id}`,
                    })),
                ]}
              />
            </>
          )}
        </div>

        {/* PhotoCaptureManager gestisce staging + webcam/upload modals + commit.
            compact=false → mostra i pulsanti Scatta e Carica.
            fase e interventionId vengono passati al backend negli upload. */}
        <PhotoCaptureManager
          ref={captureRef}
          workOrderId={workOrderId}
          fase={acquisitionFase}
          interventionId={acquisitionInterventionId}
          compact={false}
          onPhotoAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['work-order-photos', workOrderId] })
          }}
        />
      </Modal>
    </div>
  )
}

export default PhotoGallery
